import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from '@supabase/supabase-js';
import { useAuth } from "@/contexts/AuthProvider";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardOverview from "@/components/dashboard/DashboardOverview";
import CountriesTab from "@/components/dashboard/CountriesTab";
import EmployeesTab from "@/components/dashboard/EmployeesTab";
import CalendarTab from "@/components/dashboard/CalendarTab";
import GovernanceTab from "@/components/dashboard/GovernanceTab";
import RequestsTab from "@/components/dashboard/RequestsTab";
import ReportsTab from "@/components/dashboard/ReportsTab";
import WorldMapTab from "@/components/dashboard/WorldMapTab";
import SOSCenterTab from "@/components/dashboard/SOSCenterTab";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
  maximum_points_per_employee?: number;
}

const CompanyDashboard = () => {
  const { t } = useTranslation();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { session, initialized } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    console.debug('[CompanyDashboard] auth check', { initialized, hasSession: !!session });
    if (!initialized) return;
    if (!session?.user) return;
    checkUserRole(session.user);
  }, [initialized, session]);

  const checkUserRole = async (user: User) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Check role from user_roles table
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'company')
      .single();

    if (!roleData) {
      navigate('/');
      return;
    }

    // Get profile to access company_id
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!profile) {
      navigate('/');
      return;
    }

    // Check if company request is approved
    const { data: companyData } = await supabase
      .from('companies')
      .select('*')
      .eq('admin_id', profile.id)
      .single();

    if (!companyData) {
      toast({
        variant: "destructive",
        title: t("common.error", "Error"),
        description: t("dashboard.accessDenied", "Your company request has not yet been approved")
      });
      navigate('/');
      return;
    }

    setCompany(companyData);
    setLoading(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <DashboardOverview company={company} />;
      case "worldMap":
        return <WorldMapTab company={company} />;
      case "requests":
        return <RequestsTab company={company} />;
      case "employees":
        return <EmployeesTab company={company} />;
      case "countries":
        return <CountriesTab company={company} />;
      case "calendar":
        return <CalendarTab company={company} />;
      case "governance":
        return <GovernanceTab company={company} />;
      case "reports":
        return <ReportsTab company={company} />;
      case "sosCenter":
        return <SOSCenterTab company={company} />;
      default:
        return <DashboardOverview company={company} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t("common.loading", "Loading...")}</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      companyName={company?.name}
      title={`${t("dashboard.title", "HR Dashboard")} - ${company?.name || ""}`}
      subtitle={t("dashboard.subtitle", "Manage your team and workations in real-time")}
    >
      {renderTabContent()}
    </DashboardLayout>
  );
};

export default CompanyDashboard;
